import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import PMPackageDialog from './PMPackageDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';

export const PMPackageReadyBadge = ({ pkg }) =>
  pkg.ready ? (
    <Badge className="bg-green-500 hover:bg-green-600 text-white text-[10px] px-1.5 py-0">Ready ✓</Badge>
  ) : (
    <Badge className="bg-red-500 hover:bg-red-600 text-white text-[10px] px-1.5 py-0">
      {pkg.short_count} short
    </Badge>
  );

export const PMPackagePartsTable = ({ parts, testPrefix = 'pm' }) => (
  <div className="space-y-1">
    {parts.length === 0 && <div className="text-xs text-slate-500 italic">No parts in this package yet.</div>}
    {parts.map((p, idx) => (
      <div
        key={`${p.part_number}-${idx}`}
        className={`flex items-center gap-2 text-xs border rounded px-2 py-1.5 ${p.ok ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'}`}
        data-testid={`${testPrefix}-part-${idx}`}
      >
        <span className={`shrink-0 font-bold ${p.ok ? 'text-green-600' : 'text-red-600'}`}>{p.ok ? '✓' : '✗'}</span>
        <div className="flex-1 min-w-0">
          <span className="font-mono font-semibold">{p.part_number}</span>
          {p.name && <span className="text-slate-500 ml-1.5">{p.name}</span>}
          {!p.in_inventory && <span className="text-red-600 ml-1.5">(not in inventory)</span>}
        </div>
        <div className="shrink-0 text-right">
          <span className="font-medium">need {p.qty}</span>
          <span className="text-slate-500"> · have {p.available_qty}</span>
          {!p.ok && <span className="text-red-600 font-medium"> · short {p.shortfall}</span>}
        </div>
      </div>
    ))}
  </div>
);

// Per-equipment PM Packages: named parts kits with a live inventory
// availability check. Sits under Applicable Parts on the equipment page.
const PMPackagesPanel = ({ machineryId, isAdmin }) => {
  const [packages, setPackages] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('pmPackagesCollapsed') === '1'; } catch { return false; }
  });
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/machinery/${machineryId}/pm-packages`);
      setPackages(res.data || []);
    } catch { /* non-fatal */ }
  }, [machineryId]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('pmPackagesCollapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/pm-packages/${id}`);
      toast.success('PM package deleted');
      fetchPackages();
    } catch {
      toast.error('Failed to delete package');
    }
  };

  return (
    <Card data-testid="pm-packages-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={toggle}
            className="flex items-center gap-2 text-left"
            data-testid="pm-packages-toggle"
            aria-expanded={!collapsed}
          >
            <span className="text-slate-400 text-sm w-4">{collapsed ? '▸' : '▾'}</span>
            <CardTitle className="text-base">
              PM Packages
              <span className="text-sm font-normal text-slate-500 ml-2">({packages.length})</span>
            </CardTitle>
          </button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setEditingPkg(null); setDialogOpen(true); }}
              data-testid="add-pm-package-btn"
            >
              + Add
            </Button>
          )}
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="space-y-2" data-testid="pm-packages-body">
          {packages.length === 0 ? (
            <div className="text-sm text-slate-500 italic">
              No PM packages yet. Create a parts kit (e.g. &quot;8000hr Service&quot;) to check inventory readiness for each PM.
            </div>
          ) : (
            packages.map((pkg) => (
              <div key={pkg.id} className="border rounded-md" data-testid={`pm-package-${pkg.id}`}>
                <div className="flex items-center gap-2 px-2 py-2">
                  <button
                    type="button"
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    onClick={() => toggleExpand(pkg.id)}
                    data-testid={`pm-package-expand-${pkg.id}`}
                  >
                    <span className="text-slate-400 text-xs w-3">{expandedIds.has(pkg.id) ? '▾' : '▸'}</span>
                    <span className="font-medium text-sm truncate">{pkg.name}</span>
                    <PMPackageReadyBadge pkg={pkg} />
                    <span className="text-xs text-slate-400 shrink-0">{pkg.parts.length} part{pkg.parts.length === 1 ? '' : 's'}</span>
                  </button>
                  {isAdmin && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        className="text-xs text-slate-500 hover:text-slate-800 px-1"
                        onClick={() => { setEditingPkg(pkg); setDialogOpen(true); }}
                        data-testid={`edit-pm-package-btn-${pkg.id}`}
                      >
                        Edit
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button type="button" className="text-xs text-red-500 hover:text-red-700 px-1" data-testid={`delete-pm-package-btn-${pkg.id}`}>
                            Delete
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete PM Package?</AlertDialogTitle>
                            <AlertDialogDescription>
                              &quot;{pkg.name}&quot; will be removed. Inventory itself is not affected.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(pkg.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
                {expandedIds.has(pkg.id) && (
                  <div className="px-2 pb-2">
                    <PMPackagePartsTable parts={pkg.parts} testPrefix={`pm-package-${pkg.id}`} />
                  </div>
                )}
              </div>
            ))
          )}
        </CardContent>
      )}

      <PMPackageDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingPkg(null); }}
        machineryId={machineryId}
        pkg={editingPkg}
        onSaved={() => { setDialogOpen(false); setEditingPkg(null); fetchPackages(); }}
      />
    </Card>
  );
};

export default PMPackagesPanel;
